import type { HTMLAttributes, ReactNode } from 'react';

/**
 * Card composes Header / Body / Footer.  When any of those children is
 * present, the wrapping `.card` opts into `.card-flush` so its inner
 * padding becomes 0 and the sub-parts drive their own padding.
 * Otherwise the legacy `.card { padding: 18px 20px }` applies and the
 * Card acts as a drop-in elevated container.
 */

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  readonly flush?: boolean;
  readonly children?: ReactNode;
}

export function Card({ flush, className, children, ...rest }: CardProps): JSX.Element {
  const classes = ['card', flush ? 'card-flush' : '', className ?? ''].filter(Boolean).join(' ');
  return (
    <div className={classes} {...rest}>
      {children}
    </div>
  );
}

interface CardHeaderProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  readonly title?: ReactNode;
  readonly subtitle?: ReactNode;
  readonly actions?: ReactNode;
  readonly children?: ReactNode;
}

export function CardHeader({
  title,
  subtitle,
  actions,
  className,
  children,
  ...rest
}: CardHeaderProps): JSX.Element {
  return (
    <div className={['card-header', className ?? ''].filter(Boolean).join(' ')} {...rest}>
      {title || subtitle ? (
        <div className="card-header-main">
          {title ? <div className="card-title">{title}</div> : null}
          {subtitle ? <div className="card-subtitle">{subtitle}</div> : null}
        </div>
      ) : null}
      {children}
      {actions ? <div className="card-header-actions">{actions}</div> : null}
    </div>
  );
}

interface CardBodyProps extends HTMLAttributes<HTMLDivElement> {
  readonly tight?: boolean;
  readonly children?: ReactNode;
}

export function CardBody({ tight, className, children, ...rest }: CardBodyProps): JSX.Element {
  const classes = ['card-body', tight ? 'tight' : '', className ?? ''].filter(Boolean).join(' ');
  return (
    <div className={classes} {...rest}>
      {children}
    </div>
  );
}

interface CardFooterProps extends HTMLAttributes<HTMLDivElement> {
  readonly children?: ReactNode;
}

export function CardFooter({ className, children, ...rest }: CardFooterProps): JSX.Element {
  return (
    <div className={['card-footer', className ?? ''].filter(Boolean).join(' ')} {...rest}>
      {children}
    </div>
  );
}
